<template>
  <b-row style="position: relative">
    <b-col cols="12" ref="t">
      <div class="filters-container d-flex flex-row justify-content-between">
        <TableFilters
          v-model="filterOptions"
          style="margin-right: 45px"
          @click="refresh"
        />
        <div>
          <div v-if="eslot_id">
            <button
              class="btn refresh btn-sm"
              style="margin-right: 45px"
              @click.prevent="redirect"
            >
              {{ $t("Add manual report") }}
            </button>
          </div>
          <DropdownColumns v-model="fields" class="customize" />
        </div>
      </div>
      <b-table
        id="slot-reports-table"
        :busy.sync="isBusy"
        show-empty
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :hover="true"
        :striped="true"
        stacked="md"
        :items="provider"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        @row-clicked="rowClicked"
        tbody-tr-class="tr-pointer"
        v-columns-resizable="{ afterResize: columnsWidthChanged }"
      >
        <template v-slot:empty="scope">
          <div class="text-center">{{ $t(scope.emptyText) }}</div>
        </template>

        <template v-slot:head()="scope">{{ $t(scope.label) }}</template>

        <template v-slot:cell(manual)="data">
          <span v-if="data.value == 1">{{ $t("Yes") }}</span>
          <span v-else>{{ $t("No") }}</span>
        </template>
        <template v-slot:cell(requires_validation)="data">
          <span v-if="data.value == 1">{{ $t("Yes") }}</span>
          <span v-else>{{ $t("No") }}</span>
        </template>
        <template v-slot:cell(timestamp)="data">
          <span>{{ mom_unix(data.value).format(tableDateLong) }}</span>
        </template>

        <template v-slot:cell(counters_image_uuid)="data">
          <b-button
            variant="success"
            size="sm"
            v-if="data.value"
            @click="showImage(data.value)"
            style="padding: 0.25rem 0.5rem"
          >
            <i class="la la-eye" style="padding-right: 0"></i>
          </b-button>
        </template>
      </b-table>
      <div class="container-fluid">
        <div class="row">
          <div style="white-space: nowrap" class="my-2">
            <PaginationSelect
              v-model="perPage"
              :options="paginationOpts"
              variant="brand"
              size="sm"
            />
            <span>
              &nbsp;
              <i18n path="Showing {start}-{end} of {total} rows" tag="span">
                <span slot="start">{{ (currentPage - 1) * perPage + 1 }}</span>
                <span slot="end">{{ currentPage * perPage }}</span>
                <span slot="total">{{ totalRows }}</span>
              </i18n>
            </span>
            <b-button
              size="sm"
              @click.stop="
                $root.$emit('bv::refresh::table', 'slot-reports-table')
              "
              class="ml-2 refresh"
              variant="light"
              :title="$t('Refresh')"
            >
              <i class="icon-refresh"></i>
            </b-button>
          </div>
          <nav class="ml-auto mt-2">
            <b-pagination
              size="md"
              :total-rows="totalRows"
              :per-page="perPage"
              v-model="currentPage"
            />
          </nav>
        </div>
      </div>
    </b-col>
    <b-modal id="bv-modal-image" centered ok-only>
      <template slot="modal-title">{{ $t("Manual Report Image") }}</template>
      <template slot="modal-header-close">
        <span />
      </template>
      <div class="d-block" style="position: relative">
        <img :src="counters_image_uuid" style="width: 100%" title />
      </div>
    </b-modal>
    <b-modal id="bv-modal-slot-report" size="md" hide-footer centered>
      <template slot="modal-title">{{ $t("Slot Report") }}</template>
      <template slot="modal-header-close">
        <span />
      </template>
      <div class="d-block" style="position: relative">
        <b-form @submit.prevent="onAddSlotReport" autocomplete="off" ref="t">
          <b-row>
            <b-col cols="12">
              <b-row>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="lastReport.mec_in"
                    label="Previous Mec IN"
                    size="sm"
                    readonly
                  />
                </b-col>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="details.mec_in"
                    label="Mec IN"
                    size="sm"
                  />
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="lastReport.mec_out"
                    label="Previous Mec OUT"
                    size="sm"
                    readonly
                  />
                </b-col>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="details.mec_out"
                    label="Mec OUT"
                    size="sm"
                  />
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="lastReport.credit_cents"
                    label="Previous credit cents"
                    size="sm"
                    readonly
                  />
                </b-col>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="details.credit_cents"
                    label="Credit cents"
                    size="sm"
                  />
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="lastReport.counter_digits"
                    label="Previous counter digits"
                    size="sm"
                    readonly
                  />
                </b-col>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="details.counter_digits"
                    label="Counter digits"
                    size="sm"
                  />
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="lastReport.counter_rate"
                    label="Previous counter rate"
                    size="sm"
                    readonly
                  />
                </b-col>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="details.counter_rate"
                    label="Counter rate"
                    size="sm"
                  />
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="lastReport.denomination"
                    label="Previous denomination"
                    size="sm"
                    readonly
                  />
                </b-col>
                <b-col cols="12" md="6">
                  <FloatingLabel
                    v-model="details.denomination"
                    label="Denomination"
                    size="sm"
                  />
                </b-col>
              </b-row>

              <div style="position: relative">
                <div ref="imageLoader">
                  <div v-if="uploadUrl" class="text-center">
                    <div v-if="!hasImage">
                      <div style="text-align: center; width: 100%">
                        <div
                          style="
                            background-color: #fff;
                            padding: 10px;
                            min-width: 220px;
                            min-height: 220px;
                            display: inline-block;
                          "
                        >
                          <VueQRCodeComponent
                            :text="uploadUrl"
                            :size="size"
                            class="mb-1"
                            style="min-width: 200px; max-width: 200px"
                          ></VueQRCodeComponent>
                        </div>
                      </div>
                      <a
                        :href="uploadUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        >{{ uploadUrl }}</a
                      >
                    </div>
                    <div v-else>
                      <a
                        :href="uploadedURL"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img :src="uploadedURL" style="width: 100%" title />
                      </a>
                    </div>
                  </div>
                  <div v-else class="text-center mt-4 mb-4">
                    {{ $t("No upload URL") }}
                  </div>
                </div>
              </div>
              <div class="mt-4" style="text-align: right">
                <b-button
                  type="button"
                  variant="primary"
                  class="mr-2"
                  :disabled="isLoading"
                  @click.prevent="requestUpload"
                  >{{ $t("Request Upload URL") }}</b-button
                >
                <b-button type="submit" variant="success">{{
                  $t("Save")
                }}</b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </div>
    </b-modal>
  </b-row>
</template>

<script>
import moment from "moment";
import startCase from "@/shared/strings";
import { errorConf, successConf } from "@/shared/notifications";
import PaginationSelect from "@/components/PaginationSelect";
import tableMixin from "@/shared/mixins/table.mixin";
import DropdownColumns from "@/components/DropdownColumns";
import FloatingLabel from "@/components/FloatingLabel";
import Multiselect from "@/components/multiselect-filters";
import TableFilters from "@/components/TableFilters";
import timeFormat from "@/shared/timeFormat";
import axios from "axios";
import VueQRCodeComponent from "vue-qrcode-component";
import { evbus } from "@/shared/ws";

export default {
  components: {
    PaginationSelect,
    DropdownColumns,
    TableFilters,
    FloatingLabel,
    Multiselect,
    VueQRCodeComponent,
  },
  name: "slot-reports",
  props: ["eslot_id", "last_report"],
  mixins: [tableMixin],
  data: function () {
    return {
      tableName: "slot-reports",

      filterOptions: [
        {
          field: "timestamp",
          type: "date",
          opens: "right",
          timePicker: true,
          locale: {
            format: timeFormat.datePickerLong, //format of the dates displayed
            firstDay: 1, //ISO first day of week - see moment documenations for details
            showWeekNumbers: true, //show week numbers on each row of the calendar
          },
          selected: {
            startDate: moment().startOf("month"),
            endDate: moment().endOf("month"),
          },
          default: {
            startDate: moment().startOf("month"),
            endDate: moment().endOf("month"),
          },
          ranges: false,
        },
      ],

      isBusy: false,
      sortBy: "timestamp",
      sortDesc: true,
      fields: [
        {
          key: "slot_id",
          label: "Slot ID",
          sortable: false,
          class: "d-none",
          ignore: true,
        },
        {
          key: "timestamp",
          label: "Timestamp",
          sortable: true,
          class: "",
          ignore: true,
        },
        {
          key: "mec_in",
          label: "Mec IN",
          sortable: false,
          class: "",
          ignore: true,
        },
        {
          key: "delta_mec_in",
          label: "Delta Mec IN",
          sortable: false,
          class: "",
          ignore: true,
        },
        {
          key: "mec_out",
          label: "Mec OUT",
          sortable: false,
          class: "",
          ignore: true,
        },
        {
          key: "delta_mec_out",
          label: "Delta Mec OUT",
          sortable: false,
          class: "",
          ignore: true,
        },
        {
          key: "credit_cents",
          label: "Credit Cents",
          sortable: false,
          class: "",
          ignore: true,
        },
        {
          key: "counter_digits",
          label: "Counter Digits",
          sortable: false,
          class: "d-none",
          ignore: true,
        },
        {
          key: "counter_rate",
          label: "Counter Rate",
          sortable: false,
          class: "d-none",
          ignore: true,
        },
        {
          key: "denomination",
          label: "Denomination",
          sortable: false,
          class: "d-none",
          ignore: true,
        },
        {
          key: "state",
          label: "State",
          sortable: false,
          class: "",
          ignore: true,
        },
        {
          key: "manual",
          label: "Manual",
          sortable: false,
          class: "",
          ignore: true,
        },
        {
          key: "requires_validation",
          label: "Requires Validation",
          sortable: false,
          class: "",
          ignore: true,
        },
        {
          key: "counters_image_uuid",
          label: "Image",
          sortable: false,
          class: "",
          ignore: true,
        },
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,

      filter: null,
      filterField: null,

      details: {
        eslot_id: null,
        mec_in: null,
        mec_out: null,
        credit_cents: this.last_report.credit_cents,
        counter_digits: this.last_report.counter_digits,
        counter_rate: this.last_report.counter_rate,
        denomination: this.last_report.denomination,
      },

      uploadUrl: null,
      uploadedURL: null,
      urlUUID: null,
      urlID: null,
      hasImage: false,
      isLoading: false,
      counters_image_uuid: null,

      lastReport: this.last_report,
      addMode: true,
      label: "",
      size: 200,
    };
  },
  watch: {
    last_report: function (nv, ov) {
      this.lastReport = nv;
      this.details.credit_cents = nv.credit_cents;
      this.details.counter_digits = nv.counter_digits;
      this.details.counter_rate = nv.counter_rate;
      this.details.denomination = nv.denomination;
    },
  },
  created() {
    if (this.eslot_id != "0" && this.eslot_id != "") {
      this.$root.$emit("bv::refresh::table", "slot-reports-table");
    }
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      if (modalId == "bv-modal-slot-report") {
        this.details.eslot_id = this.eslot_id;
        this.details.mec_in = null;
        this.details.mec_out = null;
        this.addMode = true;
        this.hasImage = false;
        this.uploadUrl = null;
        this.uploadedURL = null;
        this.urlUUID = null;
        this.urlID = null;
      } else if (modalId == "bv-modal-image") {
        this.counters_image_uuid = null;
      }
    });
    this.wsImageUploadCB = this.wsImageUpload.bind(this);
    evbus.on("ws.notification.counters-image", this.wsImageUploadCB);
  },
  destroyed() {
    this.$root.$off("bv::modal::hide");
    evbus.remove("ws.notification.counters-image", this.wsImageUploadCB);
  },
  methods: {
    wsImageUpload(data, ev) {
      console.log(data, ev);
      if (ev == "ws.notification.counters-image" && this.urlUUID) {
        if (data.uuid === this.urlUUID) {
          this.uploadedURL =
            this.$store.getters["slot/uploadedImage"] + `/${this.urlUUID}`;
          this.hasImage = true;
        }
      }
    },
    refresh() {
      this.$root.$emit("bv::refresh::table", "slot-reports-table");
    },
    onAddSlotReport() {
      const loader = this.$loading.open({
        container: this.$refs.t,
      });

      let apiAction = "slotReport/update";
      if (this.addMode) {
        apiAction = "slotReport/create";
      }

      const detailsData = {};
      detailsData.eslot_id = this.details.eslot_id;
      if (this.details.mec_in)
        detailsData.mec_in = parseInt(this.details.mec_in, 10);
      if (this.details.mec_out)
        detailsData.mec_out = parseInt(this.details.mec_out, 10);
      detailsData.credit_cents = parseInt(this.details.credit_cents, 10);
      detailsData.counter_digits = parseInt(this.details.counter_digits, 10);
      detailsData.counter_rate = parseInt(this.details.counter_rate, 10);
      detailsData.denomination = parseFloat(this.details.denomination);
      if (this.urlUUID) detailsData.counters_image_uuid = this.urlUUID;

      this.lastReport = Object.assign({}, detailsData);

      this.$store
        .dispatch(apiAction, detailsData)
        .then((resp) => {
          let msg = "Slot report updated";
          if (this.addMode && resp.data.id) {
            msg = "Slot report created";
          }
          this.showSuccessNotification(msg);
          this.$root.$emit("bv::refresh::table", "slot-reports-table");
          this.$bvModal.hide("bv-modal-slot-report");

          this.details.mec_in = null;
          this.details.mec_out = null;
        })
        .catch((err) => {
          this.showErrorNotification(err);
        })
        .finally(() => {
          loader.close();
        });
    },
    rowClicked(item, index, event) {},
    showImage(uuid) {
      this.counters_image_uuid =
        this.$store.getters["slot/uploadedImage"] + `/${uuid}`;
      this.$bvModal.show("bv-modal-image");
    },
    redirect() {
      if (!this.eslot_id) {
        return;
      }
      this.addMode = true;
      this.details.eslot_id = this.eslot_id;
      this.$bvModal.show("bv-modal-slot-report");
    },
    provider(ctx, callback) {
      if (!this.eslot_id) {
        callback([]);
        return;
      }

      const loading = this.$loading.open({
        container: this.$refs.t,
      });

      const paginationData = {
        rowsPerPage: this.perPage,
        page: this.currentPage,
        exactFields: ["slot_id"],
        exactValues: [[this.eslot_id]],
      };

      if (this.filter && this.filterField) {
        paginationData.likeValues = [this.filter];
        paginationData.likeFields = [this.filterField];
      } else {
        paginationData.likeValues = [];
        paginationData.likeFields = [];
      }

      if (this.sortBy) {
        paginationData.sortBy = [this.sortBy];
        paginationData.sortDirection = [this.sortDesc ? "DESC" : "ASC"];
      }

      const exactFields = [];
      const exactValues = [];

      this.filterOptions.forEach((item) => {
        if (item.type != "date" && item.selected.length) {
          exactFields.push(item.field);
          exactValues.push(item.selected);
        } else if (item.type == "date") {
          if (item.selected.startDate && item.selected.endDate) {
            paginationData.dateField = item.field;
            paginationData.dateStart = item.selected.startDate;
            paginationData.dateEnd = item.selected.endDate;
          } else {
            paginationData.dateField = item.field;
            paginationData.dateStart = moment().startOf("month");
            paginationData.dateEnd = moment().endOf("month");
          }
        }
      });

      if (exactFields.length) {
        paginationData.exactFields = exactFields;
        paginationData.exactValues = exactValues;
      }

      this.$store
        .dispatch("slotReport/fetch", paginationData)
        .then((resp) => {
          this.totalRows = resp.data.total;
          callback(resp.data.rows);
        })
        .catch((err) => {
          this.$bvToast.toast(
            this.$t(err.message),
            errorConf(this.$t("Error"))
          );
          this.totalRows = 0;
          callback([]);
        })
        .finally(() => {
          loading.close();
        });

      // Must return null or undefined to signal b-table that callback is being used
      return null;
    },
    showErrorNotification(err) {
      if (err.details && err.details.length) {
        this.$bvToast.toast(
          this.$t(err.details[0]),
          errorConf(this.$t("Error"))
        );
      } else {
        this.$bvToast.toast(this.$t(err.message), errorConf(this.$t("Error")));
      }
    },
    showSuccessNotification(msg) {
      this.$bvToast.toast(this.$t(msg), successConf(this.$t("Success")));
    },
    requestUpload() {
      this.hasImage = false;
      this.uploadedURL = null;
      this.urlUUID = null;
      this.urlID = null;
      this.isLoading = true;

      const loader = this.$loading.open({
        container: this.$refs.imageLoader,
      });
      this.$store
        .dispatch("slotReport/uploadURL")
        .then((r) => {
          if (r.success) {
            this.urlUUID = r.data.uuid;
            this.urlID = r.data.eid;
            this.uploadUrl =
              location.protocol +
              "//" +
              location.host +
              "/image-upload/" +
              this.urlID;
          }
        })
        .catch((err) => {
          let verr;
          if (err.response && err.response.data) {
            if (typeof err.response.data === "string") {
              verr = err;
            } else {
              verr = err.response.data;
            }
          } else {
            verr = err;
          }
          this.showErrorNotification(verr);
        })
        .finally(() => {
          this.isLoading = false;
          loader.close();
        });
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
